import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { downloadCsv } from '@/features/data-explorer/lib/export'

import { WizardStepper } from '../components/import/wizard-stepper'
import { StepUpload, type UploadedFile } from '../components/import/step-upload'
import { StepTarget } from '../components/import/step-target'
import { ColumnMapper } from '../components/import/column-mapper'
import { ValidationGrid, type GridColumn } from '../components/import/validation-grid'
import { StepCommit, type CommitSummary } from '../components/import/step-commit'
import { PresetBar } from '../components/import/preset-bar'

import { loadPublishedDefinition, type DataIoForm } from '../api/forms'
import { runImportCommit, type ImportCommitResult } from '../api/data-import-api'
import { suggestMappings, fieldChoices } from '../lib/auto-map'
import { buildRecords } from '../lib/build-records'
import { mergeEdits } from '../lib/apply-edits'
import { validateInstance } from '../lib/validate-import'
import {
  defaultTransformFor,
  emptyMappingConfig,
  type ColumnTransform,
  type CommitStatus,
  type MappingConfig,
} from '../lib/mapping-types'

const STEPS = [
  { key: 'upload', label: 'بارگذاری' },
  { key: 'target', label: 'فرم و ساختار' },
  { key: 'map', label: 'نگاشت ستون‌ها' },
  { key: 'review', label: 'بازبینی' },
  { key: 'commit', label: 'ورود' },
]

export function DataImportPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { toast } = useToast()
  const user = useAuthStore((s) => s.user)

  const [step, setStep] = useState(0)
  const [file, setFile] = useState<UploadedFile | null>(null)
  const [form, setForm] = useState<DataIoForm | null>(null)
  const [config, setConfig] = useState<MappingConfig>(emptyMappingConfig())
  const [commitStatus, setCommitStatus] = useState<CommitStatus>('draft')
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [result, setResult] = useState<ImportCommitResult | null>(null)

  const headers = file?.parsed.headers ?? []
  const rows = useMemo(() => file?.parsed.rows ?? [], [file])

  // Load the target form's published definition.
  const { data: def, isLoading: defLoading } = useQuery({
    queryKey: ['data-io-def', form?.id],
    queryFn: () => loadPublishedDefinition(form!.id),
    enabled: !!form,
  })

  // Initialize mapping config when the form definition arrives (auto-map).
  const initedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!def || !form || headers.length === 0) return
    if (initedFor.current === form.id) return
    initedFor.current = form.id

    const choices = fieldChoices(def.fields.filter((f) => f.field_type !== 'repeatable_array'))
    const suggestions = suggestMappings(headers, choices)
    const fieldType = new Map(def.fields.map((f) => [f.field_key, f.field_type]))
    const columns = Object.entries(suggestions).map(([column, fieldKey]) => ({
      column,
      fieldKey,
      transform: defaultTransformFor(fieldType.get(fieldKey) ?? 'text'),
    }))
    setConfig({
      shape: form.instanceType === 'repeatable' ? 'repeatable_long' : 'single',
      identity: { mode: 'create_new', groupByColumns: [] },
      columns,
      wideBlocks: [],
    })
    setEdits({})
  }, [def, form, headers])

  // field_key → transform (for re-applying inline edits).
  const transformByField = useMemo(() => {
    const m = new Map<string, ColumnTransform>()
    for (const c of config.columns) if (c.fieldKey) m.set(c.fieldKey, c.transform)
    for (const b of config.wideBlocks) for (const c of b.columns) if (c.fieldKey) m.set(c.fieldKey, c.transform)
    return m
  }, [config])

  const getTransform = (fieldKey: string): ColumnTransform => {
    const t = transformByField.get(fieldKey)
    if (t) return t
    const ft = def?.fields.find((f) => f.field_key === fieldKey)?.field_type ?? 'text'
    return defaultTransformFor(ft)
  }

  const builtRecords = useMemo(() => buildRecords(rows, config), [rows, config])
  const mergedRecords = useMemo(
    () => mergeEdits(builtRecords, edits, getTransform),
    // getTransform is stable per config/def
    [builtRecords, edits, transformByField, def],
  )

  // Columns shown in the review grid = mapped field_keys.
  const gridColumns = useMemo<GridColumn[]>(() => {
    const keys = new Set<string>()
    for (const c of config.columns) if (c.fieldKey) keys.add(c.fieldKey)
    for (const b of config.wideBlocks) for (const c of b.columns) if (c.fieldKey) keys.add(c.fieldKey)
    return [...keys].map((k) => ({
      fieldKey: k,
      label: def?.fields.find((f) => f.field_key === k)?.label?.fa || k,
    }))
  }, [config, def])

  const commitSummary = useMemo<CommitSummary>(() => {
    if (!def) return { totalRecords: 0, totalInstances: 0, validInstances: 0, errorInstances: 0 }
    let total = 0
    let errs = 0
    for (const rec of mergedRecords) {
      for (const inst of rec.instances) {
        total++
        if (validateInstance(def.fields, def.conditionalRules, inst).hasBlocking) errs++
      }
    }
    return { totalRecords: mergedRecords.length, totalInstances: total, validInstances: total - errs, errorInstances: errs }
  }, [mergedRecords, def])

  const mappedCount =
    config.columns.filter((c) => c.fieldKey).length +
    config.wideBlocks.reduce((n, b) => n + b.columns.filter((c) => c.fieldKey).length, 0)

  // Per-step gating for the Next button.
  const canNext = (() => {
    switch (step) {
      case 0:
        return !!file
      case 1:
        if (!form || !def) return false
        if (config.identity.mode === 'match_display_id') return !!config.identity.displayIdColumn
        if (config.identity.mode === 'match_field') return !!config.identity.matchFieldKey && !!config.identity.matchColumn
        return true
      case 2:
        return mappedCount > 0
      case 3:
        return true
      default:
        return false
    }
  })()

  const onEditCell = (ri: number, ii: number, fieldKey: string, raw: string) => {
    setEdits((prev) => ({ ...prev, [`${ri}:${ii}:${fieldKey}`]: raw }))
  }

  const commit = async () => {
    if (!def || !form || !user || !file) return
    setBusy(true)
    setProgress({ done: 0, total: commitSummary.totalInstances })
    try {
      const res = await runImportCommit({
        projectId: projectId!,
        userId: user.id,
        formId: form.id,
        formVersionId: def.formVersionId,
        instanceType: form.instanceType,
        fields: def.fields,
        conditionalRules: def.conditionalRules,
        records: mergedRecords,
        commitStatus,
        fileName: file.name,
        fileFormat: file.format,
        onProgress: (done, total) => setProgress({ done, total }),
      })
      setResult(res)
      if (res.failed === 0 && res.skipped === 0) {
        toast({ title: 'ورود کامل شد', description: `${res.submissionsCreated + res.submissionsUpdated} ثبت ایجاد/به‌روز شد.` })
      } else {
        toast({ variant: 'destructive', title: 'ورود با خطاهای جزئی', description: `${res.failed + res.skipped} مورد وارد نشد.` })
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'خطا در ورود', description: (e as Error).message })
    } finally {
      setBusy(false)
    }
  }

  const downloadReport = () => {
    if (!result) return
    const headers = ['source_row', 'action', 'display_id', 'error']
    const matrix = result.rows.map((r) => [String(r.sourceRowIndex + 1), r.action, r.displayId ?? '', r.error ?? ''])
    downloadCsv(`import-report-${new Date().toISOString().slice(0, 10)}.csv`, headers, matrix)
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="ورود داده"
        breadcrumbs={[{ label: 'ورود و خروجی داده', href: `/projects/${projectId}/data-io` }, { label: 'ورود' }]}
      />

      <div className="border-b border-border bg-card px-6 py-3 overflow-x-auto">
        <WizardStepper steps={STEPS} current={step} onJump={(i) => !result && i < step && setStep(i)} />
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto">
          {step === 0 && <StepUpload file={file} onParsed={(f) => { setFile(f); initedFor.current = null }} />}

          {step === 1 && projectId && (
            <StepTarget
              projectId={projectId}
              headers={headers}
              form={form}
              def={def ?? null}
              defLoading={defLoading}
              config={config}
              commitStatus={commitStatus}
              onForm={(f) => { setForm(f); initedFor.current = null }}
              onConfig={setConfig}
              onCommitStatus={setCommitStatus}
            />
          )}

          {step === 2 && def && projectId && form && (
            <div className="space-y-4">
              <PresetBar projectId={projectId} formId={form.id} config={config} onApply={setConfig} />
              <ColumnMapper headers={headers} rows={rows} fields={def.fields} config={config} onConfig={setConfig} />
            </div>
          )}

          {step === 3 && def && (
            <ValidationGrid
              records={mergedRecords}
              columns={gridColumns}
              fields={def.fields}
              conditionalRules={def.conditionalRules}
              edits={edits}
              onEditCell={onEditCell}
            />
          )}

          {step === 4 && (
            <StepCommit
              summary={commitSummary}
              commitStatus={commitStatus}
              busy={busy}
              progress={progress}
              result={result}
              onCommit={commit}
              onDownloadReport={downloadReport}
            />
          )}
        </div>
      </div>

      {/* Footer nav */}
      {!result && (
        <div className="border-t border-border bg-card px-6 py-3 flex items-center justify-between">
          <Button variant="outline" disabled={step === 0 || busy} onClick={() => setStep((s) => s - 1)} className="gap-1">
            <ChevronRight className="w-4 h-4" /> قبلی
          </Button>
          {step < STEPS.length - 1 && (
            <Button disabled={!canNext} onClick={() => setStep((s) => s + 1)} className="gap-1">
              بعدی <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
